import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { DayStrip } from "@/components/DayStrip";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty, fmtDate } from "@/components/ui-kit";
import { Pager, todayStr } from "@/components/Pager";
import { AttachmentList } from "@/components/Attachments";
import { StudentPicker, ChildChips } from "@/components/StudentPicker";
import { downloadDailyReport } from "@/lib/reportCardPdf";
import { BulkUpdateForm } from "@/components/BulkUpdateForm";
import { ApprovalBadge, RejectReason } from "@/components/ApprovalBadge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Baby, FileDown, Pencil, Trash2, ImagePlus, Loader2 } from "lucide-react";

const FIELDS = [["food_intake", "Food intake"], ["nap_time", "Nap time"], ["washroom", "Washroom"], ["classroom_activities", "Classroom activities"], ["learning_activities", "Learning activities"],
  ["behavior", "Behaviour"], ["health_observation", "Health observation"], ["teacher_remarks", "Teacher remarks"]];
const blank = () => ({ student_id: "", date: todayStr(), photos: [], ...Object.fromEntries(FIELDS.map(([k]) => [k, ""])) });

function PhotoPicker({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  async function pick(e) {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    e.target.value = "";
    setBusy(true);
    const out = [];
    for (const f of files) {
      try { const fd = new FormData(); fd.append("file", f); out.push((await api.post("/upload/attachment", fd)).data); }
      catch (err) { toast.error(`${f.name}: ${formatApiError(err)}`); }
    }
    onChange([...value, ...out].slice(0, 8)); setBusy(false);
  }
  return (
    <div className="space-y-2">
      <label className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 border border-violet-200 rounded-lg px-3 py-1.5 cursor-pointer hover:bg-violet-50">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} Add photos
        <input data-testid="su-photos-input" type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={pick} />
      </label>
      <AttachmentList items={value} onRemove={(i) => onChange(value.filter((_, j) => j !== i))} />
    </div>
  );
}

function UpdateForm({ editing, onDone }) {
  const [f, setF] = useState(blank());
  useEffect(() => { setF(editing ? { ...blank(), ...editing } : blank()); }, [editing]);
  async function save() {
    const body = { student_id: f.student_id, date: f.date, photos: f.photos, ...Object.fromEntries(FIELDS.map(([k]) => [k, f[k]])) };
    try {
      if (editing) await api.put(`/student-updates/${editing.id}`, body); else await api.post("/student-updates", body);
      toast.success(editing ? "Update saved" : "Update posted — parents notified"); setF(blank()); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div data-testid="su-form" className="bg-white rounded-2xl shadow-sm p-5 mb-6 space-y-3">
      <h3 className="font-display font-bold text-slate-900">{editing ? `Edit update • ${editing.student_name}` : "Post a daily student update"}</h3>
      {!editing && <StudentPicker value={f.student_id} onChange={(v) => setF((x) => ({ ...x, student_id: v }))} />}
      <div><Label>Date</Label><div className="mt-1"><DayStrip testid="su-date" max={todayStr()} value={f.date} onChange={(v) => v && setF({ ...f, date: v })} /></div></div>
      <div className="grid md:grid-cols-2 gap-3">
        {FIELDS.map(([k, l]) => <div key={k}><Label>{l}</Label><Textarea data-testid={`su-${k}`} rows={2} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1" /></div>)}
      </div>
      <PhotoPicker value={f.photos} onChange={(v) => setF({ ...f, photos: v })} />
      <div className="flex gap-2">
        <Button data-testid="su-save" disabled={!f.student_id || !f.date} onClick={save} className="btn-brand">{editing ? "Save changes" : "Post update"}</Button>
        {editing && <Button variant="ghost" onClick={() => onDone()} data-testid="su-cancel">Cancel</Button>}
      </div>
    </div>
  );
}

function ReportCard({ u, canEdit, onEdit, onDelete }) {
  const [busy, setBusy] = useState(false);
  return (
    <div data-testid={`su-card-${u.id}`} className="bg-white rounded-2xl shadow-sm overflow-hidden">
      <div className="bg-violet-600 text-white px-4 py-2.5 flex flex-wrap items-center gap-2">
        <span className="font-display font-bold">{u.student_name}</span>
        <span className="text-xs text-white/80">{u.class_name}-{u.section} • {fmtDate(u.date)} • {u.teacher_name}</span>
        {u.approval && <span className="bg-white rounded-full"><ApprovalBadge status={u.approval} testid={`su-status-${u.id}`} /></span>}
        <div className="ml-auto flex gap-1.5">
          <Button size="sm" variant="secondary" data-testid={`su-pdf-${u.id}`} disabled={busy} onClick={async () => { setBusy(true); await downloadDailyReport(u).catch(() => toast.error("PDF failed")); setBusy(false); }}>
            {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <FileDown size={14} className="mr-1" />} Report card PDF</Button>
          {canEdit && <Button size="sm" variant="secondary" data-testid={`su-edit-${u.id}`} onClick={onEdit}><Pencil size={14} /></Button>}
          {canEdit && <Button size="sm" variant="secondary" data-testid={`su-delete-${u.id}`} onClick={onDelete} className="text-rose-600"><Trash2 size={14} /></Button>}
        </div>
      </div>
      <div className="p-4 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
        {FIELDS.filter(([k]) => u[k]).map(([k, l]) => (
          <div key={k} className={k.includes("activities") || k === "teacher_remarks" ? "sm:col-span-2" : ""}>
            <div className="text-[11px] uppercase tracking-wide font-semibold text-violet-700">{l}</div><p className="text-sm text-slate-700 whitespace-pre-line">{u[k]}</p>
          </div>
        ))}
        {u.photos?.length > 0 && <div className="sm:col-span-2"><AttachmentList items={u.photos} /></div>}
        <div className="sm:col-span-2"><RejectReason item={u} /></div>
      </div>
    </div>
  );
}

export default function StudentUpdatesPage() {
  const { user } = useAuth();
  const isParent = user?.role === "parent";
  const [children, setChildren] = useState([]);
  const [child, setChild] = useState("");
  const [day, setDay] = useState("");
  const [markedDays, setMarkedDays] = useState([]);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [mode, setMode] = useState("class");
  const [data, setData] = useState({ items: [], page: 1, pages: 1, total: 0 });
  useEffect(() => { if (isParent) api.get("/parent/children").then((r) => { setChildren(r.data); setChild(r.data[0]?.id || ""); }).catch(() => {}); }, [isParent]);
  const load = () => {
    api.get("/student-updates/dates", { params: { student_id: child || undefined } }).then((r) => setMarkedDays(r.data)).catch(() => {});
    return api.get("/student-updates", { params: { page, day: day || undefined, student_id: child || undefined } }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { if (!isParent || child) load(); }, [page, day, child]); // eslint-disable-line react-hooks/exhaustive-deps
  const isTeacher = user?.role === "teacher";
  const canEdit = (u) => isTeacher && u.teacher_id === user.id;
  async function del(u) { if (!window.confirm("Delete this update?")) return; try { await api.delete(`/student-updates/${u.id}`); load(); } catch (e) { toast.error(formatApiError(e)); } }
  return (
    <AppShell>
      <PageHeader eyebrow="Daily report" title={isParent ? "My Child's Day" : "Student Daily Updates"} subtitle={isParent ? "Food, nap, activities, behaviour and health — every day." : isTeacher ? "Post each student's day. It reaches parents after Coordinator and Principal approve." : "View submitted daily activities. Teachers fill these in; approve them under Approvals."} />
      {isParent ? <ChildChips children={children} value={child} onChange={(v) => { setChild(v); setPage(1); }} /> : isTeacher && (<>
        <Tabs value={editing ? "single" : mode} onValueChange={(v) => { setMode(v); setEditing(null); }} className="mb-4">
          <TabsList className="bg-violet-100"><TabsTrigger value="class" data-testid="su-mode-class">Whole class</TabsTrigger><TabsTrigger value="single" data-testid="su-mode-single">One student</TabsTrigger></TabsList>
        </Tabs>
        {mode === "class" && !editing ? <BulkUpdateForm onDone={load} /> : <UpdateForm editing={editing} onDone={() => { setEditing(null); load(); }} />}
      </>)}
      <div className="mb-4">
        <DayStrip testid="su-filter-date" marked={markedDays} countLabel="updates"
          preview={(d) => api.get("/student-updates", { params: { day: d, student_id: child || undefined, limit: 50 } }).then((r) => r.data.items.map((x) => ({ id: x.id, title: x.student_name, sub: `${x.class_name}-${x.section} • by ${x.teacher_name}` })))} value={day} onChange={(v) => { setDay(v); setPage(1); }} clearLabel="All dates" />
      </div>
      {data.items.length === 0 ? <Empty testid="su-empty" icon={Baby} text={isParent && !children.length ? "No child linked to your account yet." : "No daily updates yet."} /> : (
        <div className="space-y-4">{data.items.map((u) => <ReportCard key={u.id} u={u} canEdit={canEdit(u)} onEdit={() => { setEditing(u); window.scrollTo({ top: 0, behavior: "smooth" }); }} onDelete={() => del(u)} />)}</div>
      )}
      <Pager page={data.page} pages={data.pages} total={data.total} onPage={setPage} testid="su-pager" />
    </AppShell>
  );
}
